import { create } from 'zustand';

export interface CheckCaptureCredentials {
  frontS3Key: string;
  frontUploadUrl: string;
  rearS3Key: string;
  rearUploadUrl: string;
}

export interface CheckCaptureImages {
  frontImage?: string;
  rearImage?: string;
}

export interface ImageValidationError {
  front: boolean;
  message: string;
  rear: boolean;
}

export interface CheckCaptureSubmission {
  checkSelected: boolean;
  imageValidationError: ImageValidationError | null;
}

interface CheckCaptureState {
  clearCheckSelection: () => void;
  clearImages: () => void;
  clearImageValidationError: (side: 'front' | 'rear') => void;
  commitCheckSelection: () => void;
  credentials: CheckCaptureCredentials | null;
  images: CheckCaptureImages;
  reset: () => void;
  setCredentials: (credentials: CheckCaptureCredentials | null) => void;
  setImages: (images: CheckCaptureImages) => void;
  setImageValidationError: (error: ImageValidationError | null) => void;
  submission: CheckCaptureSubmission;
}

const EMPTY_SUBMISSION: CheckCaptureSubmission = {
  checkSelected: false,
  imageValidationError: null,
};

/**
 * Holds state for the check-capture workflow, grouped into three sections:
 * - `credentials`: server-issued S3 destinations + keys from prepare. Stored
 *   while the action sheet is open so retake attempts reuse them; cleared on
 *   close or successful submit.
 * - `images`: captured front/rear image bytes.
 * - `submission`: user intent (`checkSelected`) and per-side image validation
 *   feedback from a failed submit attempt.
 *
 * @example
 * const credentials = useCheckCaptureStore(selectCredentials);
 * const setCredentials = useCheckCaptureStore((s) => s.setCredentials);
 */
export const useCheckCaptureStore = create<CheckCaptureState>()((set) => ({
  clearCheckSelection: () =>
    set((state) => ({
      submission: { ...state.submission, checkSelected: false },
    })),
  clearImages: () => set({ images: {} }),
  clearImageValidationError: (side) =>
    set((state) =>
      state.submission.imageValidationError
        ? {
            submission: {
              ...state.submission,
              imageValidationError: {
                ...state.submission.imageValidationError,
                [side]: false,
              },
            },
          }
        : state,
    ),
  commitCheckSelection: () =>
    set({
      submission: { checkSelected: true, imageValidationError: null },
    }),
  credentials: null,
  images: {},
  reset: () =>
    set({
      credentials: null,
      images: {},
      submission: EMPTY_SUBMISSION,
    }),
  setCredentials: (credentials) => set({ credentials }),
  setImages: (images) => set({ images }),
  setImageValidationError: (error) =>
    set((state) => ({
      submission: { ...state.submission, imageValidationError: error },
    })),
  submission: EMPTY_SUBMISSION,
}));

/**
 * Returns the cached credentials, or `null` if prepare hasn't completed.
 * A non-null result lets the prepare effect skip the round-trip on re-entry.
 *
 * @example
 * const credentials = useCheckCaptureStore(selectCredentials);
 * if (!credentials) { runPrepare(); }
 */
export const selectCredentials = (
  state: CheckCaptureState,
): CheckCaptureCredentials | null => state.credentials;

/**
 * Returns the captured images (front/rear). Empty `{}` until first capture.
 *
 * @example
 * const { frontImage, rearImage } = useCheckCaptureStore(selectImages);
 */
export const selectImages = (state: CheckCaptureState): CheckCaptureImages =>
  state.images;
